import { ReactNode } from "react";
import { Leaf } from "lucide-react";
import { BottomNavigation } from "./BottomNavigation";
import { TopBar } from "./TopBar";
import { motion } from "framer-motion";

/** Единый заголовок приложения для вкладок: текст + иконка листа (зелёная). */
export const APP_HEADER_TITLE = "Mom Recipes";
export const APP_HEADER_ICON = (
  <Leaf className="w-5 h-5 shrink-0 text-primary" aria-hidden />
);

/**
 * LAYOUT CONTRACT (safe-area / отступы)
 * -------------------------------------
 * - Верхний отступ: задаётся ТОЛЬКО через main-below-header (или main-safe-top без хедера).
 *   НЕ добавлять на страницах: pt-safe, pt-[env(safe-area-inset-top)], дублирующий pt-* для "под хедер".
 * - Нижний отступ: задаётся ТОЛЬКО через main-above-nav при showNav.
 *   НЕ добавлять на страницах: pb-safe для "над таббаром", pb-20/pb-24 под нав — main уже учитывает.
 * - Высота хедера: ровно --layout-header-offset (safe-area + контент). Не менять padding/height внутри хедера.
 * - Хедер: title строго по центру; headerLeft — absolute left, headerRight — absolute right (top-right угол). Ничто не сдвигает заголовок.
 * - Viewport: использовать min-h-dvh / 100dvh, не 100vh, чтобы не прыгало при адресной строке.
 * - Скролл: один контейнер на странице (обычно первый ребёнок main с overflow-y-auto). Не скроллить body и вложенный div одновременно.
 */
interface MobileLayoutProps {
  children: ReactNode;
  /** Если не заданы title, headerCenter, headerLeft, headerRight и headerBrand — хедер не рендерится (корневые вкладки без TopBar). */
  title?: string;
  showNav?: boolean;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  /** Optional meta line below title (e.g. recipe page: age · meal type · cook time) */
  headerMeta?: ReactNode;
  /** Иконка справа от заголовка (например лист для Mom Recipes) */
  headerTitleIcon?: ReactNode;
  /** Left block: по умолчанию пусто; false = не показывать, ReactNode = кастом (headerLeft тоже можно передать) */
  headerBrand?: ReactNode | false;
  /** Кастомный контент по центру вместо title (например логотип) */
  headerCenter?: ReactNode;
  /** Убрать блюр у хедера (только фон) */
  headerNoBlur?: boolean;
  /** Доп. классы для header (например граница) */
  headerClassName?: string;
  /** Разрешить многострочный title в header и авто-высоту шапки. */
  headerWrapTitle?: boolean;
  /** Доп. классы для main (например отступ под высокий хедер рецепта) */
  mainClassName?: string;
}

export function MobileLayout({
  children,
  title,
  showNav = true,
  headerLeft,
  headerRight,
  headerMeta,
  headerTitleIcon,
  headerBrand = undefined,
  headerCenter,
  headerNoBlur = false,
  headerClassName,
  headerWrapTitle = false,
  mainClassName,
}: MobileLayoutProps) {
  const showHeader =
    (title != null && title !== "") ||
    headerCenter != null ||
    headerLeft != null ||
    headerRight != null ||
    (headerBrand != null && headerBrand !== false);
  const leftContent = headerBrand === false ? headerLeft : (headerBrand ?? headerLeft);
  const baseHeaderClass = headerNoBlur
    ? "sticky top-0 z-40 bg-background border-b border-border/50 layout-header"
    : "sticky top-0 z-40 bg-background/95 backdrop-blur-lg border-b border-border/50 layout-header";

  return (
    <div className="h-dvh min-h-dvh bg-background flex flex-col overflow-hidden">
      {showHeader ? (
        <header className={headerClassName ? `${baseHeaderClass} ${headerClassName}` : baseHeaderClass}>
          <div className="layout-header-inner">
            <TopBar
              left={leftContent != null && leftContent !== false ? leftContent : undefined}
              center={
                headerCenter ??
                (title != null && title !== "" ? (
                  <>
                    <div className="flex items-center justify-center gap-1.5 min-w-0 w-full">
                      <h1
                        className={
                          headerWrapTitle
                            ? "w-full text-[17px] font-semibold text-foreground leading-tight whitespace-normal break-words [overflow-wrap:anywhere]"
                            : "text-xl font-semibold text-foreground truncate leading-tight"
                        }
                      >
                        {title}
                      </h1>
                      {headerTitleIcon}
                    </div>
                    {headerMeta != null && (
                      <p
                        className={
                          headerWrapTitle
                            ? "text-xs text-muted-foreground w-full mt-0.5 whitespace-normal break-words [overflow-wrap:anywhere]"
                            : "text-xs text-muted-foreground truncate w-full mt-0.5"
                        }
                      >
                        {headerMeta}
                      </p>
                    )}
                  </>
                ) : undefined)
              }
              multilineCenter={headerWrapTitle}
              right={headerRight ?? undefined}
            />
          </div>
        </header>
      ) : null}
      <motion.main
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className={`flex flex-col min-h-0 flex-1 overflow-y-auto main-scroll-contain ${showHeader ? "main-below-header" : "main-safe-top"} ${showNav ? "main-above-nav" : ""} ${mainClassName ?? ""}`}
      >
        {children}
      </motion.main>
      {showNav && <BottomNavigation />}
    </div>
  );
}
